import { DynamicRegulation } from "../../../Models/Compliance/DynamicRegulation";
import { FileTableEntry } from "../../../Models/Documents/DocumentModels";
import { SimpleInventory } from "../../../Models/Inventories/SimpleInventory";
import {
    Questionnaire,
    SurveyRequestInventoryMetadata,
    QuestionnairePage,
    QuestionnairePageType,
    SurveyRequest,
    SurveyResponseModel,
    SurveyResponseInventoryGroup,
    GroupedResponseInventories,
    BomRevisionRequest,
    QuestionRegulatoryData,
    InventoryRow,
    ComplianceRegulationDetails,
    ScipSubmissionData
} from "../../../Models/Surveys/SurveyDataModel";
import {ResultType} from "../../../Models/Api/ApiResponse";
import { DataCollectionGapSummaryMetadata } from "../../../Models/Api/DataCollectionGapSummaryModels";
import { InventoryQuestionnaireType } from "./SurveyResponseStates";
import {StandardComplianceScoreAsNumber} from "../../../Models/SharedMaterials/DataModel";

// Enum representing the type of AI agent operation
export enum DataCollectionAiAgentOperationType {
    Survey = 0,
    OfflineResponse = 1
}

// Enum representing the agent operation code
export enum DataCollectionAiAgentOperationCode {
    ReadingSurveyQuestions = 0,
    ReadingDocuments = 1,
    ConsultingRegulatoryDatabase = 2,
    GeneratingAnswerSuggestions = 3
}

// Enum representing the state of AI agent operation
export enum DataCollectionAiAgentOperationState {
    Triggered = 0,
    RequestInProgress = 1,
    ResponseReceived = 2,
    ResponseProcessed = 3,
    Failed = 4,
    TerminatedByExchange = 5,
    DeleteInProgress = 6,
    DeleteCompleted = 7,
    TerminatedByUser = 8,
    PreparingGapSummary = 9
}

export const OfflineAiResponseAlreadyProvidedResult = "OfflineAiResponseAlreadyProvided";
export const SupplierResponseAlreadyProvidedResult = "SupplierResponseAlreadyProvided";
export const ResponseAlreadyProvidedMessage = "A response has already been provided.";
export const SurveyResponseSubmittedOfflineImportUnavailableMessage = "Survey response has already been submitted by supplier and offline import is unavailable.";

export interface SupplierActionAvailability {
    reason: number;
    canActivate: boolean;
    canEdit: boolean;
    canSubmit: boolean;
    resultCode: string;
    message: string;
}

export enum ProductStatus {
    Unanswered = 1,
    Incomplete = 2,
    Complete = 3
}

export enum CertificationType {
    Other = "Other",
    C2c = "C2c",
    Declare = "Declare",
    Epd = "Epd",
    Hpd = "Hpd",
    Voc = "Voc",
    UlGreenGuard = "UlGreenGuard",
    UlGreenGuardGold = "UlGreenGuardGold",
    ScsIndoorAdvantage = "ScsIndoorAdvantage",
    ScsIndoorAdvantageGold = "ScsIndoorAdvantageGold",
    BimfaLevel = "BimfaLevel",
    EnergyStar = "EnergyStar",
    SaferChoice = "SaferChoice",
    Lpc = "Lpc",
    Nsf140 = "Nsf140",
    Nsf332 = "Nsf332",
    GreenCircleCertifiedEnvironmentalFacts = "GreenCircleCertifiedEnvironmentalFacts",
    GreenCircleCertifiedSustainabilityFacts = "GreenCircleCertifiedSustainabilityFacts",
    UlEcologo = "UlEcologo",
    AnsiNsc373 = "AnsiNsc373",
    UlProductLens = "UlProductLens",
    GreenCircleCertifiedManufacturerInventoryVerification = "GreenCircleCertifiedManufacturerInventoryVerification",
    C2cMaterialHealth = "C2cMaterialHealth",
    Greenscreen = "Greenscreen",
    GreenHealthApproved = "GreenHealthApproved",
    GreenSeal = "GreenSeal",
    CdphStandardMethodv122017 = "CdphStandardMethodv122017",
    Floorscore = "Floorscore",
    CriGreenLabelPlus = "CriGreenLabelPlus",
    MasCertifiedGreen = "MasCertifiedGreen",
    IntertekCleanAir = "IntertekCleanAir",
    TscaTitleViNaf = "TscaTitleViNaf",
    TscaTitleViUlef = "TscaTitleViUlef",
    Lca = "Lca",
    GreenCircleCertifiedLifeCycleAssessmentOptimized = "GreenCircleCertifiedLifeCycleAssessmentOptimized",
    ScsCarbonNeutral = "ScsCarbonNeutral",
    GreenCircleCertifiedEnergySavings = "GreenCircleCertifiedEnergySavings",
    FcsCertified = "FcsCertified",
    SfiCertified = "SfiCertified",
    UsdaCertifiedBiobased = "UsdaCertifiedBiobased",
    GreenCircleCertifiedBiobasedContent = "GreenCircleCertifiedBiobasedContent",
    UlEnvironmentalClaimValidationBiobasedContent = "UlEnvironmentalClaimValidationBiobasedContent",
    GreenCircleCertifiedRecycledContent = "GreenCircleCertifiedRecycledContent",
    UlEnvironmentalClaimValidationRecycledContent = "UlEnvironmentalClaimValidationRecycledContent",
    ScsRecycledContentCertified = "ScsRecycledContentCertified",
    GlobalRecycledStandard = "GlobalRecycledStandard",
    GreenCircleCertifiedClosedLoopProduct = "GreenCircleCertifiedClosedLoopProduct",
    GreenCircleCertifiedRecyclableMaterial = "GreenCircleCertifiedRecyclableMaterial",
    UsEpdWaterSense = "UsEpdWaterSense"
}

export interface Certification {
    name: string;
    certificationExternalId: string;
    type: CertificationType;
    expirationDate: string;
    metadata: {
        level: string;
    }
}

export interface SafeUseInstructionsScip {
    safeUseInstructions: string;
    noSafeUseInstructionRequired: boolean;
}


export interface PaginatedList<T> {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    items: T[];
}

export interface ExtensionRequestModel {
    extensionRequestId: string | null;
    responseId: string;
    responseOwnerId: string;
    extensionDate: string | null;
    comment: string | null;
    inventoryIds: string[];
}

export interface InventoryIdentifiers {
    inventoryId: string;
    name : string;
    description : string;
    productNumber : string;
    supplierPartNumber : string;
}

export interface CoreSurveyResponseData {
    requestorManufacturerLogoUrl: string | null;
    response: SurveyResponseModel;
    request: SurveyRequest | null;
    inventoryInfoTable: { [inventoryId: string]: SimpleInventory };
    allSupplierFiles: FileTableEntry[];
    materialIdsDataToLoadList: string[];
    revisionReqInvNames: string[];
    needsSupplierLevelQuestionnaireResponse: boolean;
    needsMaterialResponse: boolean;
    complianceInfoNeeded: string;
    includeAttestationLetter: boolean;
    attestationLetterFileId: string;
    requestableColumnIdToFriendlyNameMap: { [columnId: string]: string };
    extensionRequest: ExtensionRequestModel;
    totalInventories: number | 0;  // Total number of inventories
    totalInventoriesAfterGrouping: number | 0;
    hasGroups: boolean | false;  
    totalPages: number | 0;  // Total number of pages
    page: number | 0;     // Current page number
    size: number;
    requestorTargetLibraryInventoryIdentifiers: InventoryIdentifiers[];
    supplierActionAvailability: SupplierActionAvailability;
    aiOperation: AiSurveyOperation | null;
}

export interface AiSurveyOperation {
    ownerId: string | null;
    requestId: string | null;
    operationId: string | null;
    userWhoInitiatedTheRequest: string | null;
    type: DataCollectionAiAgentOperationType | null;
    state: DataCollectionAiAgentOperationState | null;
    agentOperationCode: DataCollectionAiAgentOperationCode | null;
    agentState: string | null;
    fileIds: string[] | null;
    isDataOverriden: boolean | null;
    dateInitiated: string | null;
    dateModified: string | null;
    dateResponseReceived: string | null;
    dateCompleted: string | null;
    responseOperationId: string | null;
    metadata?: AiSurveyOperationMetadata | null;
    webRetrievalConfig?: AiWebRetrievalConfig | null;
    gapSummary: DataCollectionGapSummaryMetadata | null;
    queueTasksAhead?: number | null;
}

export interface AiSurveyOperationMetadata {
    autoAcceptOfflineResponse?: boolean;
    documentPagesProcessed?: number;
    questionsAsked?: number;
    aiAnswersProvided?: number;
    aiAnswersUpdated?: number;
    materialsRequested?: number;
    materialsProcessed?: number;
    highConfidenceAnswers?: number;
    lowConfidenceAnswers?: number;
    insufficientDataAnswers?: number;
    terminationReason?: string;
}

export interface AiWebRetrievalConfig {
    enabled: boolean;
    urls: string[];
    userNotes: string;
}

// Helper function to check if AI operation is in readonly states
export const isAiProcessingReadonly = (aiOperation: AiSurveyOperation | null): boolean => {
    if (!aiOperation || aiOperation.state === null || aiOperation.state === undefined) {
        return false;
    }

    const readonlyAiStates = [
        DataCollectionAiAgentOperationState.Triggered,
        DataCollectionAiAgentOperationState.RequestInProgress,
        DataCollectionAiAgentOperationState.ResponseReceived,
        DataCollectionAiAgentOperationState.PreparingGapSummary,
        DataCollectionAiAgentOperationState.DeleteInProgress
    ];

    const stateValue = aiOperation.state as unknown;
    const enumValue = typeof stateValue === 'string'
        ? DataCollectionAiAgentOperationState[stateValue as keyof typeof DataCollectionAiAgentOperationState]
        : stateValue;

    return typeof enumValue === 'number' && readonlyAiStates.includes(enumValue);
};

// Helper function to check if survey should be readonly based on response state or AI operation states
export const isSurveyReadonly = (coreData: CoreSurveyResponseData | null, aiOperation?: AiSurveyOperation | null): boolean => {
    if (!coreData?.response) return false;

    // Check if response is submitted (state === 1)
    if (coreData.response.state === 1) return true;

    if (coreData.supplierActionAvailability?.canEdit === false) return true;

    if (!coreData.needsSupplierLevelQuestionnaireResponse && !coreData.needsMaterialResponse) return true;

    // Check if AI operation is in readonly states
    if (aiOperation) {
        return isAiProcessingReadonly(aiOperation);
    }

    return false;
};

export interface InventoryDetailsResponse {
    inventoryId: string;
    questionnairesTypes: InventoryQuestionnaireType[];
    questionnaireList: Questionnaire[];
    fileList: FileTableEntry[] | null;
    hasBomPage: boolean;
    name: string;
    description: string;
    productNumber: string;
    supplierPartNumber: string;
    photoFileId: string;
    revisionRequest: BomRevisionRequest;
    bomNextClicked?: boolean;
    questionRegulatoryData: QuestionRegulatoryData[];
    inventoryRows: InventoryRow[];
    applicableRegulatoryLists: ComplianceRegulationDetails[];

    requestorOwnedInventoryId: string;
    scipSubmissionData: ScipSubmissionData | null;
    extensibleColumnNames: string[];
    validationType: string;
}

export interface PlausibilityData {
    question: string;
    answer: string;
    confidence: string;
    shortText: string;
    longText: string;
}

export interface SurveyInventories {
    productsList: PaginatedList<SurveyRequestInventoryMetadata>;
}

export interface InventoryResponses {
    questionnaire: Questionnaire;
    customQuestionnaire: Questionnaire | undefined;
    customAttestationQuestionnaire: Questionnaire | undefined;
}

export interface SurveyResponseValidationResult {
    result: ResultType | string;
    inventoryIds: string[];
    optionalQuestionsInventoryIds: string[];
    isOverallValid: boolean;
    supplierQuestionnaireStatus: boolean;
    // TODO: large-surveys // only store status of current page of materials
    bomStatusTable: {
        [inventoryId: string]: BomStatusItem;
    };
    supplierQuestionnaireStatusList: PageQuestionnaireStatuses[];
}

export interface BomStatusItem {
    friendlyName: string;
    fieldsStatus: boolean;
    questionnaireStatus: boolean;
    complianceDataStatus: boolean;
    sustainabilityDataStatus: boolean;
    circularityDataStatus: boolean;
    documentsDataStatus: boolean;
    sdsDataStatus: boolean;
    isLinkedSharedMaterial: boolean;
    accessLevel: string;
    allValidationsPassed: boolean;
    attestationQuestionnaireStatus: boolean;
    questionnairesStatuses: QuestionnairesStatuses;
    inventoryBomValidation: BomValidationStatus[];
}

export interface QuestionnairesStatuses {
    attestationQuestionnaireStatus: PageQuestionnaireStatuses[];
    customQuestionnaireStatus: PageQuestionnaireStatuses[];
    baseQuestionnaireStatus: PageQuestionnaireStatuses[];
}

export interface BomValidationStatus {
    rowIndex: number;
    message: string;
    columnId: string;
}

export interface PageQuestionnaireStatuses {
    page: QuestionnairePageType | string;
    pageIsComplete: boolean;
    questions: QuestionValidationStatus[];
    answerIsRemaining: boolean;
}

export interface QuestionValidationStatus {
    question: string;
    valid: boolean;
}

export type WriteGroupedResponseInventoriesResult = GroupedResponseInventories;

export enum ComplianceScoreNodeType {
    material,
    substance
}

export enum ComplianceScore {
    Unknown,
    Compliant,
    NonCompliant
}

export interface ComplianceScoreResult {
    standardOverallScore: ComplianceScore;
    regulationSpecificOverallScore: string;
    standardProductScore: ComplianceScore;
    regulationSpecificProductScore: string;
    standardMaterialsScore: ComplianceScore;
    regulationSpecificMaterialsScore: string;
    aggregateDefinitiveNodePercentRange: string;
    materialsRootNode: ComplianceMaterialSummaryNode;
}
export interface PaginationDetails {
    totalInventories: number;
    totalInventoriesAfterGrouping: number;
    totalPages: number;
    hasGroups: boolean;
}
export type RegulationScoresTable = { [regulationId: string]: ComplianceScoreResult | null };

// ===============================================================================

//
// Represents the compliance determination for a single node in an inventory tree.
// The nodes are used to determine a Materials Score for the product which accounts
// for both linked material-level summary data in the Compliance Response, and
// regulation substance thresholds.
// See C# class for more property details.
//
export interface ComplianceMaterialSummaryNode {
    name: string;
    linkedInventoryId: string;
    nodeRowIndex: number;
    casNumber: string;
    substanceId: string;
    standardScore: StandardComplianceScoreAsNumber;
    specificRegulationScore: string;
    flaggedListIds: string[];
    productPercentByWeight: string;
    hasProductPercentByWeight: boolean;
    type: ComplianceScoreNodeType;
    summaryTable: { [summaryDataKey: string]: string };
    children: ComplianceMaterialSummaryNode[];
    isDeterminedBySummaryData: boolean;
    isRegulationApplicable: boolean;
    hasDescendentNonComplianceOrUnknown: boolean;
}
